'use client';

import React, { Fragment, useEffect } from 'react';
import { useHydrateAtoms } from 'jotai/utils';
import { readStreamableValue, StreamableValue } from 'ai/rsc';
import { streamContent } from '../../app/actions/streamSubtopicContent';

import {
  desktopSidebar,
  mapDataAtom,
  contentAtom,
  mapViewAtom,
  isStreamingContentAtom,
  streamContentErrorAtom,
  isStreamingMapAtom,
  streamMapErrorAtom,
  chaptersOpenStatesAtom,
  newMapDataAtom,
  overviewAtom,
  pass1LearningPathAtom,
  pass2LearningPathAtom,
  jobTitleAtom,
  isLoadingNavAtom,
  streamingContentSlugAtom,
} from '../../jotaiStore';
import { chapterAtom } from '../../jotaiStore/chaptersStore';
import { useAtom, useAtomValue } from 'jotai';
import { useParams } from 'next/navigation';
import { OVERVIEW_SLUG, WHATS_NEXT_SLUG } from '../../constants/constants';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { deslugify } from '../../lib/map/slugify';
import {
  Pass1LearningPath,
  CareerMap,
  Pass2Section,
  Pass2Sidequest,
  Pass2Option,
  Chapter,
} from '../../typesV2';

import { streamOverview } from '../../app/actions/streamOverview';
import getActiveSubtopicIndexes from '../../lib/map/getActiveSubtopicIndex';
import { runContentStream } from '../../lib/streams/runContentStream';
import { runMapPass1Stream } from '../../lib/streams/runMapPass1Stream';
import { runMapPass2Stream } from '../../lib/streams/runMapPass2Stream';
import { runOverviewStream } from '../../lib/streams/runOverviewStream';
import { saveMap } from '../../lib/db/saveMapNew';
import { updateMapCache } from '../../app/actions/updateMapCache';
import { streamWhatsNext } from '@/app/actions/streamWhatsNext';
import { runStreamWhatsNext } from '@/lib/streams/runStreamWhatsNext';
import { toast } from 'react-toastify';
import { dismissNavigationWarningToast } from '@/lib/toast';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1, // Only retry failed queries once
      refetchOnWindowFocus: false, // Disable automatic refetch on window focus
    },
  },
});

const getInitialContent = (initChapter: Chapter | null, initMap: CareerMap | null) => {
  if (!initChapter || !initMap) {
    return '';
  }

  if (initChapter.type === 'overview') {
    return initMap.jobDescription;
  }

  if (initChapter.type === 'whats-next') {
    return initMap.whatsNext;
  }

  if (
    initChapter.type === 'section' &&
    'topicIndex' in initChapter &&
    'subtopicIndex' in initChapter
  ) {
    const topic = initMap.topicList[initChapter.topicIndex];
    return topic?.sections?.[initChapter.subtopicIndex]?.content ?? '';
  }

  if (
    initChapter.type === 'option' &&
    'topicIndex' in initChapter &&
    'subtopicIndex' in initChapter
  ) {
    const topic = initMap.topicList[initChapter.topicIndex];
    return topic?.options?.[initChapter.subtopicIndex]?.content ?? '';
  }

  if (
    initChapter.type === 'sidequest' &&
    'topicIndex' in initChapter &&
    'subtopicIndex' in initChapter
  ) {
    const topic = initMap.topicList[initChapter.topicIndex];
    return topic?.sideQuests?.[initChapter.subtopicIndex]?.content ?? '';
  }

  return '';
};

const TriggerProvider: React.FC<{
  children: React.ReactNode;
  map: CareerMap | null;
  subtopic: Pass2Section | Pass2Option | Pass2Sidequest | null;
  chapter?: Chapter;
  shouldTriggerStream?: boolean;
}> = ({ children, map, subtopic, chapter, shouldTriggerStream }) => {
  const { mapSlug, contentSlug } = useParams();
  const shouldContinueStreaming = React.useRef(true);

  // Reset streaming flag when contentSlug changes
  React.useEffect(() => {
    shouldContinueStreaming.current = true;
    return () => {
      shouldContinueStreaming.current = false;
    };
  }, [contentSlug]);

  const isSubtopicSelected = chapter && chapter.type !== 'overview';

  const initialContent = chapter ? getInitialContent(chapter, map) : '';

  useHydrateAtoms([
    [mapDataAtom, map ?? null],
    [jobTitleAtom, map?.jobTitle ?? null],
    [pass2LearningPathAtom, map?.topicList ?? null],
    [desktopSidebar, true],
    [mapViewAtom, false],
    [contentAtom, initialContent ?? ''],
    [chapterAtom, chapter],
    [chaptersOpenStatesAtom, [true]],
    [isStreamingMapAtom, false],
    [streamMapErrorAtom, null],
    [isLoadingNavAtom, false],
  ]);

  // const [, setNewMapData] = useAtom(newMapDataAtom);
  const [, setJobTitle] = useAtom(jobTitleAtom);
  const [pass1LearningPath, setPass1LearningPath] = useAtom(pass1LearningPathAtom);
  const [pass2LearningPath, setPass2LearningPath] = useAtom(pass2LearningPathAtom);
  const [, setContentText] = useAtom(contentAtom);
  // const [, setOverview] = useAtom(overviewAtom);
  const [, setIsStreamingContent] = useAtom(isStreamingContentAtom);
  const [, setStreamContentError] = useAtom(streamContentErrorAtom);
  const [, setIsStreamingMap] = useAtom(isStreamingMapAtom);
  const [, setStreamMapError] = useAtom(streamMapErrorAtom);
  const [, setChapter] = useAtom(chapterAtom);
  const [, setIsShowingMap] = useAtom(mapViewAtom);
  const isStreamingMap = useAtomValue(isStreamingMapAtom);

  useEffect(() => {
    if (!isStreamingMap) {
      dismissNavigationWarningToast();
    }
  }, [isStreamingMap]);

  React.useEffect(() => {
    const runStreams = async () => {
      // this is because we have multiple trigger providers in the app but only one should trigger the stream
      if (shouldTriggerStream) {
        if (!map && !pass2LearningPath) {
          // console.log('TRIGGER PROVIDER: STREAM MAP');
          /***************************************************/
          /******************  STREAM MAP  ******************/
          /***************************************************/
          setIsStreamingMap(true);
          setChapter({
            type: 'overview',
          });
          const jobTitle = deslugify(mapSlug as string);
          setJobTitle(jobTitle);
          try {
            const pass1FinalLearningPath: Pass1LearningPath =
              await runMapPass1Stream(
                setPass1LearningPath,
                jobTitle,
                mapSlug as string
              );

            // Run overview and pass2 streams concurrently
            const [overview, pass2FinalLearningPath] = await Promise.all([
              runOverviewStream(
                setContentText,
                setIsStreamingContent,
                jobTitle,
                pass1FinalLearningPath
              ),
              runMapPass2Stream(
                (pass2LearningPath) => {
                  setPass2LearningPath(pass2LearningPath);
                },
                jobTitle,
                mapSlug as string,
                pass1FinalLearningPath
              ),
            ]);

            const mapWithSlugifiedSubtopics = await saveMap({
              mapSlug: mapSlug as string,
              jobTitle: jobTitle,
              overview: overview,
              learningPath: pass2FinalLearningPath,
            });
            await updateMapCache(mapSlug as string, mapWithSlugifiedSubtopics);

            // if (!process.env.NEXT_PUBLIC_SKIP_SAVE_TO_USER) {
            // const mapWithSlugifiedSubtopics = await saveMap({
            //   mapSlug: mapSlug as string,
            //   jobTitle: jobTitle,
            //   overview: overview,
            //   learningPath: pass2FinalLearningPath,
            // });
            // UPDATE CACHE
            // process.env.NEXT_PUBLIC_SKIP_CACHE === 'false' &&
            // await updateMapCache(mapSlug as string, mapWithSlugifiedSubtopics);
            // }

            // setMapData(map);
            setIsStreamingMap(false);
            toast.dismiss('NO_NAVIGATION_WHILE_STREAMING');
            // toast.info('Loading map complete', {
            //   toastId: 'LOADING_MAP_COMPLETE',
            //   theme: 'dark',
            //   hideProgressBar: true,
            //   autoClose: 3000,
            // });
          } catch (err) {
            console.error('Error fetching map:', err);
            setStreamMapError(err as Error);
          }
        } else if (
          contentSlug &&
          contentSlug !== OVERVIEW_SLUG &&
          contentSlug !== WHATS_NEXT_SLUG &&
          !subtopic?.content &&
          !!map
        ) {
          // console.log('TRIGGER PROVIDER: STREAM CONTENT');
          /***************************************************/
          /******************  STREAM CONTENT  ******************/
          /***************************************************/
          const contentSlugString = contentSlug as string;
          const activeChapter = getActiveSubtopicIndexes({
            mapData: map,
            contentSlug: contentSlugString,
            chapter: chapter,
          });
          setChapter(activeChapter);
          setIsStreamingContent(true);
          await runContentStream(
            setContentText,
            map as CareerMap,
            subtopic as Pass2Section | Pass2Option | Pass2Sidequest,
            activeChapter,
            shouldContinueStreaming
          )
            .then(([content, error]) => {
              if (error) {
                console.error('Error fetching content:', error);
                setStreamContentError(error);
                // !todo Handle error state
              }
              setIsStreamingContent(false);
            })
            .catch((err) => {
              console.error('Error fetching content:', err);
              // setStreamContentError(err);
              // !todo Handle error state
            });
        } else if (
          contentSlug &&
          contentSlug === WHATS_NEXT_SLUG &&
          !map?.whatsNext &&
          !!map
        ) {
          // console.log('TRIGGER PROVIDER: STREAM WHATS NEXT');
          // stream whats next
          setChapter({
            type: 'whats-next',
          });
          setIsStreamingContent(true);
          await runStreamWhatsNext(setContentText, setIsStreamingContent, map);
          setIsStreamingContent(false);
        } else if (map && contentSlug === WHATS_NEXT_SLUG && map.whatsNext) {
          // console.log('TRIGGER PROVIDER: WHATS NEXT CACHE HIT');
          setChapter({
            type: 'whats-next',
          });
          setContentText(map.whatsNext);
        } else if (map && !contentSlug) {
          // console.log('TRIGGER PROVIDER: OVERVIEW CACHE HIT');
          // setMapData(map);

          setChapter({
            type: 'overview',
          });
          setJobTitle(map.jobTitle);
          setContentText(map.jobDescription);
          setPass2LearningPath(map.topicList);
          setIsStreamingContent(false);
          setIsStreamingMap(false);
        } else {
          // console.log('TRIGGER PROVIDER: CACHE HIT');
          /***************************************************/
          /******************  CACHE HIT  ******************/
          /***************************************************/
          // is this where we hit an existing maps overview?
          // setMapData(camelcaseKeys(map as unknown as Record<string, unknown>));
          if (contentSlug && contentSlug !== OVERVIEW_SLUG && !!map) {
            setContentText(subtopic?.content ?? '');
            const activeChapter = getActiveSubtopicIndexes({
              mapData: map,
              contentSlug: contentSlug as string,
              chapter: chapter,
            });

            setChapter(activeChapter);
          } else {
            // console.log('TRIGGER PROVIDER: OVERVIEWWWWWWWW');
            setChapter({
              type: 'overview',
            });
          }
        }
      }
    };
    runStreams();
  }, [
    map,
    subtopic,
    setContentText,
    setIsStreamingContent,
    setStreamContentError,
    setIsStreamingMap,
    setStreamMapError,
    mapSlug,
    // setMapData,
    contentSlug,
    shouldTriggerStream,
    setChapter,
  ]);

  return (
    <Fragment>
      <QueryClientProvider client={queryClient}>
        {children}
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </Fragment>
  );
};

export default TriggerProvider;
